import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { add, deleteRecord, fetch, update } from '../services/BillableUnitsService';
import { BillableUnit } from '../types';
import { notifySuccess, notifyError } from '../../../components/ServiceNotifications';

export const useBillableUnit = (billableUnitId?: string) => {
    const queryClient = useQueryClient();

    const billableUnitQuery = useQuery({
        queryKey: ['billable-unit', billableUnitId],
        queryFn: () => fetch(billableUnitId!),
        enabled: !!billableUnitId
    });

    const createMutation = useMutation({
        mutationFn: (values: BillableUnit) => {
            const { created: _created, updated: _updated, ...rest } = values;
            return add(rest);
        },
        onSuccess: () => {
            notifySuccess('Billable Unit created');
            queryClient.invalidateQueries({ queryKey: ['billable-units'] });
        },
        onError: (error) => {
            notifyError(error);
        }
    });

    const updateMutation = useMutation({
        mutationFn: (values: BillableUnit) => {
            if (!billableUnitId) throw new Error('billableUnitId is required parameter');
            const { created: _created, updated: _updated, ...rest } = values;
            return update(billableUnitId, rest);
        },
        onSuccess: () => {
            notifySuccess('Billable Unit updated');
            queryClient.invalidateQueries({ queryKey: ['billable-units'] });
            queryClient.invalidateQueries({ queryKey: ['billable-unit', billableUnitId] });
        },
        onError: (error) => {
            notifyError(error);
        }
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!billableUnitId) throw new Error('billableUnitId is required parameter');
            return deleteRecord(billableUnitId);
        },
        onSuccess: () => {
            notifySuccess('Billable Unit deleted');
            queryClient.invalidateQueries({ queryKey: ['billable-units'] });
        },
        onError: (error) => {
            notifyError(error);
        }
    });

    return {
        record: billableUnitQuery.data,
        loading: billableUnitQuery.isLoading,
        fetchRecord: billableUnitQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation
    };
};
